import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Wallet, CreditCard, Banknote, FileText, FileCheck2, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { TESTS, ADDITIONAL_TESTS, OTHER_SERVICES, ONLINE_PAYMENTS_ENABLED, SITE } from "../mock";

const ALL_TESTS = [...TESTS, ...ADDITIONAL_TESTS];

const paymentMethods = [
  { icon: Banknote, label: "Cash" },
  { icon: CreditCard, label: "Debit / Credit Card" },
  { icon: FileText, label: "Cheques" },
];

const included = [
  "Official ETS test administration by certified proctors",
  "Results available in two business days",
  "Official score report accepted by WORC",
  "Test center located conveniently in George Town",
];

export default function Pricing() {
  return (
    <div data-testid="pricing-page">
      <div className="bg-[#0A2540] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div className="text-[#39C1D0] font-bold text-sm uppercase tracking-widest mb-3">Pricing</div>
          <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">Simple, Transparent Test Pricing</h1>
          <p className="mt-5 text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Payment must be made on or before check in. No hidden fees — the price you see is the price you pay.
          </p>
        </div>
      </div>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
            {ALL_TESTS.map((t, idx) => (
              <Card
                key={t.id}
                data-testid={`pricing-card-${t.id}`}
                className={`rounded-2xl shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col ${
                  idx === 0 ? "border-2 border-[#0E9AAA] relative" : "border border-slate-100"
                }`}
              >
                {idx === 0 && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <Badge className="bg-[#0E9AAA] text-white hover:bg-[#0E9AAA] font-bold px-4 shadow-md">Most Popular</Badge>
                  </div>
                )}
                <CardContent className="p-8 flex flex-col flex-1">
                  <h3 className="font-extrabold text-[#0A2540] text-lg leading-snug">{t.shortName}</h3>
                  <p className="text-xs text-slate-500 mt-1.5">{t.badge}</p>
                  <div className="mt-6 flex items-baseline gap-1.5">
                    <span className="text-4xl font-extrabold text-[#0A2540]">CI ${t.price}</span>
                    <span className="text-slate-400 text-sm font-semibold">/ test</span>
                  </div>
                  <ul className="mt-6 space-y-2.5 text-sm text-slate-600 flex-1">
                    <li className="flex items-start gap-2"><CheckCircle2 size={15} className="text-[#0E9AAA] mt-0.5 shrink-0" /> {t.schedule}</li>
                    <li className="flex items-start gap-2"><CheckCircle2 size={15} className="text-[#0E9AAA] mt-0.5 shrink-0" /> {t.duration}</li>
                    <li className="flex items-start gap-2"><CheckCircle2 size={15} className="text-[#0E9AAA] mt-0.5 shrink-0" /> {t.results}</li>
                    <li className="flex items-start gap-2"><CheckCircle2 size={15} className="text-[#0E9AAA] mt-0.5 shrink-0" /> {t.scoreScale}</li>
                  </ul>
                  <Link to={`/book?test=${t.id}`} className="mt-7">
                    <Button
                      className={`w-full font-bold rounded-lg h-11 transition-colors ${
                        idx === 0
                          ? "bg-[#0E9AAA] hover:bg-[#0C8695] text-white shadow-md"
                          : "bg-[#0A2540] hover:bg-[#123B63] text-white"
                      }`}
                    >
                      Book Now <ArrowRight size={16} className="ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-16">
            <h2 className="text-lg font-extrabold text-[#0A2540] uppercase tracking-wide mb-6">Additional Services</h2>
            {OTHER_SERVICES.map((s) => (
              <Card key={s.id} data-testid={`service-card-${s.id}`} className="rounded-2xl border-slate-100 shadow-sm">
                <CardContent className="p-8">
                  <div className="flex flex-col md:flex-row md:items-center gap-6">
                    <div className="w-14 h-14 rounded-xl bg-[#E8F7F9] border border-[#B5E6EC] flex items-center justify-center shrink-0">
                      <FileCheck2 className="text-[#0E9AAA]" size={26} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-extrabold text-[#0A2540] text-lg">{s.name}</h3>
                      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.description}</p>
                      <p className="mt-2 text-xs text-slate-500 flex items-center gap-1.5">
                        <Phone size={13} className="text-[#0E9AAA]" /> {s.availability} {SITE.phone} • {SITE.email}
                      </p>
                    </div>
                    <div className="md:text-right shrink-0">
                      <div className="font-extrabold text-[#0A2540] text-2xl" data-testid={`service-price-${s.id}`}>CI ${Number(s.price).toFixed(2)}</div>
                      <div className="text-xs text-slate-400 font-semibold mt-1">per copy</div>
                      {ONLINE_PAYMENTS_ENABLED && (
                        <Link to="/certified-copy">
                          <Button data-testid="request-copy-btn" className="mt-3 bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-lg">
                            Request & Pay Online <ArrowRight size={15} className="ml-1.5" />
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-16 grid lg:grid-cols-2 gap-8">
            <Card className="rounded-2xl border-slate-100 shadow-sm">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-5">
                  <Wallet className="text-[#0E9AAA]" size={24} />
                  <h3 className="font-extrabold text-[#0A2540] text-xl">Accepted Payment Methods</h3>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  {paymentMethods.map((p) => (
                    <div key={p.label} className="bg-[#F6FAFB] border border-slate-100 rounded-xl p-5 text-center">
                      <p.icon className="text-[#0E9AAA] mx-auto mb-2.5" size={26} />
                      <div className="text-sm font-bold text-[#0A2540]">{p.label}</div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-5">
                  Payment must be made on or before check in. Testees who have not completed payment will not be seated.
                </p>
                <p className="text-xs text-slate-500 mt-2 font-semibold" data-testid="card-fee-note">
                  Please note: a 2% transaction fee applies to all card and online payments.
                </p>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border-slate-100 shadow-sm">
              <CardContent className="p-8">
                <h3 className="font-extrabold text-[#0A2540] text-xl mb-5">Every Test Includes</h3>
                <ul className="space-y-3.5">
                  {included.map((i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
                      <CheckCircle2 size={17} className="text-[#0E9AAA] mt-0.5 shrink-0" /> {i}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
